// src/admin/pages/KPI/components/kpi/TrendModal.js
import React, { useMemo, useState, useEffect } from "react";
import Modal from "react-modal";
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useMediaQuery } from "react-responsive";

const SIDEBAR_WIDTH = 240;
const MONTHS = ["01","02","03","04","05","06","07","08","09","10","11","12"];

/* ------- helpers ------- */
const toMin = (s) => Number((s / 60).toFixed(2));   // sec → min (2 dec)

/* metrice espresse in secondi (da convertire)  */
const TIME_METRICS = new Set([
  "avg_session_duration",       // ← aggiunta
  "avg_prep_time",
  "avg_order_delivery_time",
  "avg_queue_time",
  "avg_total_time",
]);

const splitDate = (iso) => {
  const d = new Date(iso);
  const y = d.getFullYear().toString();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  return { y, m, label: `${m}/${y.slice(2)}` };
};

export default function TrendModal({
  open,
  onClose,
  label,
  metricKey,
  data = [],
  /* ristorante */
  restaurantsList = [],
  selectedRestaurant = "all",
  onRestaurantChange,
  /* area (cucina/bar) */
  area = "all",           // "all" | "kitchen" | "bar"
  onAreaChange,           // funzione(newArea)
}) {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTimeMetric = TIME_METRICS.has(metricKey);

  /* ----- filtro anni ----- */
  const yearsAvailable = useMemo(() => {
    const s = new Set(data.map((d) => splitDate(d.period).y));
    return Array.from(s).sort();
  }, [data]);

  const [yearsSelected, setYearsSelected] = useState([]);
  useEffect(() => setYearsSelected(yearsAvailable), [yearsAvailable]);

  const toggleYear = (yr) =>
    setYearsSelected((prev) =>
      prev.includes(yr) ? prev.filter((y) => y !== yr) : [...prev, yr]
    );

  /* ----- lookup e griglia ----- */
  const valueMap = useMemo(() => {
    const m = {};
    data.forEach((d) => {
      const { y, m: mo } = splitDate(d.period);
      m[y] ??= {};
      m[y][mo] = d.value;
    });
    return m;
  }, [data]);

  const chartData = useMemo(() => {
    const activeYears = yearsSelected.length ? yearsSelected : yearsAvailable;
    const rows = [];
    activeYears.forEach((y) =>
      MONTHS.forEach((mo) => {
        const raw = valueMap[y]?.[mo] ?? 0;
        rows.push({
          month: `${mo}/${y.slice(2)}`,
          value: isTimeMetric ? toMin(raw) : raw,
        });
      })
    );
    return rows;
  }, [yearsSelected, yearsAvailable, valueMap, isTimeMetric]);

  /* ----- UI ----- */
  const showAreaBar =
    isTimeMetric && metricKey !== "avg_session_duration";

  const modalStyle = {
    overlay: { background: "rgba(0,0,0,0.4)", zIndex: 1000 },
    content: {
      inset: isMobile ? "10% 5%" : `10% 5% 10% ${SIDEBAR_WIDTH + 20}px`,
      padding: "1.5rem",
      border: "none",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      overflow: "auto",
    },
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={modalStyle}
      ariaHideApp={false}
    >
      {/* ─── Header & filtro ristorante ─── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center" }}>
        <h2 style={{ margin: 0, flexGrow: 1 }}>{label}</h2>

        {restaurantsList.length > 1 && (
          <select
            value={selectedRestaurant}
            onChange={(e) => onRestaurantChange?.(e.target.value)}
            style={{ padding: "0.4rem", borderRadius: "6px", border: "1px solid #ccc" }}
          >
            <option value="all">Tutti i ristoranti</option>
            {restaurantsList.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* ─── filtro area (solo per tempo-metric, escluso session) ─── */}
      {showAreaBar && (
        <div style={styles.areaBar}>
          {[
            { key: "all", label: "Tutti" },
            { key: "kitchen", label: "Cucina" },
            { key: "bar", label: "Bar" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => opt.key !== area && onAreaChange?.(opt.key)}
              style={{
                ...styles.areaBtn,
                ...(area === opt.key ? styles.areaBtnActive : {}),
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* ─── filtro anni ─── */}
      <div style={{ marginTop: "0.8rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {yearsAvailable.map((yr) =>
          yearsAvailable.length > 1 ? (
            <label key={yr} style={{ display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={yearsSelected.includes(yr)}
                onChange={() => toggleYear(yr)}
              />
              <span style={{ marginLeft: "0.3rem" }}>{yr}</span>
            </label>
          ) : (
            <span key={yr} style={{ fontWeight: 600 }}>
              {yr}
            </span>
          )
        )}
      </div>

      {/* ─── grafico ─── */}
      <ResponsiveContainer width="100%" height={340} style={{ marginTop: "1rem" }}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis
            allowDecimals={false}
            tickFormatter={(v) => v.toLocaleString("it-IT")}
            label={
              isTimeMetric
                ? { value: "min", position: "insideLeft", angle: -90 }
                : undefined
            }
          />
          <Tooltip formatter={(v) => v.toLocaleString("it-IT")} />
          <Legend />
          <Bar
            dataKey="value"
            name={label + (isTimeMetric ? " (min)" : "")}
            barSize={32}
            radius={[4, 4, 0, 0]}
            fill="#FF8C00"
          />
        </BarChart>
      </ResponsiveContainer>

      {/* ─── chiudi ─── */}
      <button
        onClick={onClose}
        style={{
          marginTop: "1rem",
          padding: "0.6rem 1.2rem",
          border: "none",
          borderRadius: "6px",
          background: "#336699",
          color: "#fff",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Chiudi
      </button>
    </Modal>
  );
}

/* ---------- styles ---------- */
const styles = {
  areaBar: {
    display: "inline-flex",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
    margin: "1rem 0",
  },
  areaBtn: {
    flex: "1 1 0",
    padding: ".45rem .9rem",
    border: "none",
    background: "#F5F5F5",
    color: "#333",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background .15s, color .15s",
  },
  areaBtnActive: {
    background: "#336699",
    color: "#FFF",
  },
};
